import JSZip from "jszip";
import axiosInstant from "../api";
import { getBrandProject, getBrandProjectAllMessages } from "../api/endpoints";
import { buildChannelFolderResolver } from "./downloadHelpers";
import { CommentGroup, CsvScope, commentsCsvBlob } from "./commentsCsv";
import {
    VideoComment,
    commentsPdfBlob,
    getReviewComments,
} from "./commentsPdfGenerator";
import { downloadPdfBlob } from "./pdf/tdcPdfTheme";

export type CommentsFormat = "pdf" | "csv" | "all";

interface LineageGroup {
    fileIds: number[];
    comments: VideoComment[];
}

interface CommentedVideo {
    videoName: string;
    channel?: string;
    comments: VideoComment[];
}

const labelDuplicates = (videos: CommentedVideo[]): CommentedVideo[] => {
    const nameCounts = videos.reduce<Record<string, number>>((acc, v) => {
        acc[v.videoName] = (acc[v.videoName] || 0) + 1;
        return acc;
    }, {});
    const channelCounts = videos.reduce<Record<string, number>>((acc, v) => {
        if (v.channel) acc[v.channel] = (acc[v.channel] || 0) + 1;
        return acc;
    }, {});

    return videos.map((video) => {
        if (nameCounts[video.videoName] < 2 || !video.channel) return video;
        const channelIsUnique = channelCounts[video.channel] === 1;
        return {
            ...video,
            videoName: channelIsUnique
                ? video.channel
                : `${video.videoName} (${video.channel})`,
        };
    });
};

export interface ProjectComments {
    projectTitle: string;
    productTitle?: string;
    videos: CommentedVideo[];
}

const FILENAME_UNSAFE = /[\\/:*?"<>|]/g;

const cleanName = (value: string | undefined, fallback: string): string =>
    (value || fallback)
        .replace(FILENAME_UNSAFE, " ")
        .replace(/\s+/g, " ")
        .trim() || fallback;

export const fetchProjectComments = async (
    projectId: number,
    projectFiles: any[],
    channels: any[] | null | undefined,
    projectTitle: string,
    productTitle?: string,
): Promise<ProjectComments> => {
    const { data } = await axiosInstant.get(getBrandProjectAllMessages(projectId));
    const groups: LineageGroup[] = data?.groups ?? [];
    const folderFor = buildChannelFolderResolver(channels);

    const videos: CommentedVideo[] = [];
    const used = new Set<LineageGroup>();
    for (const file of projectFiles ?? []) {
        const group = groups.find((entry) => entry.fileIds.includes(file.id));
        if (!group || used.has(group)) continue;
        used.add(group);
        const comments = getReviewComments(group.comments, { includeDeleted: true });
        if (comments.length === 0) continue;
        videos.push({
            videoName: file.name || file.title || `Video ${file.id}`,
            channel: folderFor(file),
            comments,
        });
    }
    return { projectTitle, productTitle, videos: labelDuplicates(videos) };
};

export const fetchProjectCommentsById = async (
    projectId: number,
    projectTitle?: string,
): Promise<ProjectComments> => {
    const { data } = await axiosInstant.get(getBrandProject(projectId));
    return fetchProjectComments(
        projectId,
        data?.projectFiles ?? [],
        data?.content_channels,
        projectTitle || data?.title || "Project",
        data?.product?.title,
    );
};

const toGroups = (projects: ProjectComments[]): CommentGroup[] =>
    projects.flatMap((project) =>
        project.videos.map((video) => ({
            videoName: video.videoName,
            projectTitle: project.projectTitle,
            comments: video.comments,
        })),
    );

const deliver = async (
    projects: ProjectComments[],
    format: CommentsFormat,
    baseName: string,
    title: string,
    scope: CsvScope,
    subtitle?: string,
): Promise<boolean> => {
    const groups = toGroups(projects);
    if (groups.length === 0) return false;

    const pdf = async () =>
        commentsPdfBlob({
            videos: groups.map((g) => ({ videoName: g.videoName, comments: g.comments })),
            title,
            subtitle,
        });

    if (format === "pdf") {
        downloadPdfBlob(await pdf(), `${baseName}.pdf`);
        return true;
    }
    if (format === "csv") {
        downloadPdfBlob(commentsCsvBlob(groups, scope), `${baseName}.csv`);
        return true;
    }

    const zip = new JSZip();
    zip.file(`${baseName}.pdf`, await pdf());
    zip.file(`${baseName}.csv`, commentsCsvBlob(groups, scope));
    downloadPdfBlob(await zip.generateAsync({ type: "blob" }), `${baseName}.zip`);
    return true;
};

export const downloadVideoComments = (
    video: { videoName: string; comments: VideoComment[] },
    format: CommentsFormat,
    projectTitle: string,
    productTitle?: string,
): Promise<boolean> =>
    deliver(
        [{ projectTitle, productTitle, videos: [video] }],
        format,
        `${cleanName(video.videoName, "Video")} Video Comments`,
        projectTitle || video.videoName,
        {},
        productTitle,
    );

export const downloadProjectComments = (
    project: ProjectComments,
    format: CommentsFormat,
): Promise<boolean> =>
    deliver(
        [project],
        format,
        `${cleanName(project.projectTitle, "Project")} Project Video Comments`,
        project.projectTitle,
        { withVideo: true },
        project.productTitle,
    );

export const downloadOrderComments = (
    orderName: string,
    projects: ProjectComments[],
    format: CommentsFormat,
): Promise<boolean> =>
    deliver(
        projects,
        format,
        `${cleanName(orderName, "Order")} Order Video Comments`,
        orderName,
        { withVideo: true, withProject: true },
    );
